import { e, getDueDateUrgency, formatRelativeTime, formatShortDate, parseContext, confidenceGlyph, normalizeStatus, linkify } from './modules/utils.js';
import { backgroundFetch } from './modules/network.js';
import { CTX_TAGS } from './modules/state.js';
import { SESSION_KEY, SESSION_USER_KEY, getAuthHeader } from './modules/auth-client.js';
import { loadOverrides, saveOverrides } from './modules/storage.js';
import { isArtifactSnoozed, snoozeArtifact, writeDelegateHistory, refreshDelegateQueue, showDiscardUndoToast, showSendConfirmModal, openDelegatePrompt, enqueueCardDelegate, showDelegateToast } from './modules/delegate-client.js';
import { boardData, navStack, setNavStack, expandedCards, AI_NAME, _commsCache, fetchComms } from './core.js';
import { getStatusStyle, collectAllContextTags } from './ui.js';
import { findCardById, mergeOverrides, getCardSchedule } from './data.js';
import { duplicateCard, toggleArchive, isParentCard } from './cards.js';
// Circular dep — safe because called at runtime only
import { renderFromState } from './render.js';

// ── Module-private state ──────────────────────────────────────────────────────

// Workspace state
let _activeWorkspace = null;
let _workspacePollTimer = null;

// Wins state
let _winsMode = false;

// Review state
let _reviewMode = false;
let _reviewStep = 0;
let _reviewCards = null;
let _reviewCardIdx = 0;

// Work mode state
let _workModeCardId = null;
let _workModeTaskIdx = 0;
let _workModeTimer = null;
let _workModeSeconds = 0;

// Intentions state
let _intentions = [];
let _intentionIdx = 0;
let _intentionTimerId = null;
let _intentionsActive = false;
let _intentionTransitionPending = false;

// Focus mode state
let _focusMode = false;

// Focus Flow state (§9b)
let _focusFlowActive = false;
let _focusPhase = 0; // 0=idle, 1=quick-wins, 2=intentions, 3=deep-work
let _focusQueue = [];
let _focusQueueIdx = 0;
let _focusCompletedCount = 0;
let _focusStartedAt = 0;
let _focusTimerCheck = null;
let _focusIntentionCols = [];
let _focusIntentionColIdx = 0;
let _focusSkippedIds = new Set();
let _focusTimeCapReached = false;

// Keyboard navigation state
let _kbCardIdx = -1;

// ── Getters ───────────────────────────────────────────────────────────────────

export function getActiveWorkspace() { return _activeWorkspace; }
export function isIntentionsActive() { return _intentionsActive; }
export function isFocusMode() { return _focusMode; }
export function isReviewMode() { return _reviewMode; }
export function isWinsMode() { return _winsMode; }
export function isFocusFlowActive() { return _focusFlowActive; }

// ── Card Workspace Mode ───────────────────────────────────────────────────

export function openWorkspace(cardId, opts) {
    opts = opts || {};
    if (_activeWorkspace) closeWorkspace();
    const merged = mergeOverrides(boardData);
    const proj = findCardById(cardId, merged);
    if (!proj) return;
    // Find column for this card
    let cardColumn = '';
    for (const col of merged.columns) {
      for (const p of (col.projects || [])) {
        if (p.id === cardId) { cardColumn = col.name; break; }
        if (p.children) { for (const c of p.children) { if (c.id === cardId) { cardColumn = col.name; break; } } }
      }
    }

    const cardEl = document.querySelector(`.card[data-project-id="${cardId}"]`);
    const rect = cardEl ? cardEl.getBoundingClientRect() : { left: window.innerWidth/2 - 300, top: window.innerHeight/2 - 200, width: 600, height: 400 };

    // Scrim
    const scrim = document.createElement('div');
    scrim.id = 'workspace-scrim';
    scrim.style.cssText = `position:fixed;inset:0;background:rgba(0,0,0,0.55);backdrop-filter:blur(2px);z-index:798;opacity:0;transition:opacity 150ms ease`;
    document.body.appendChild(scrim);

    // Workspace container
    const ws = document.createElement('div');
    ws.id = 'card-workspace';
    ws.style.cssText = `position:fixed;z-index:799;background:var(--surface);border:1px solid var(--border);border-radius:12px;box-shadow:0 16px 64px rgba(0,0,0,0.5);display:flex;flex-direction:column;overflow:hidden;transition:all 220ms cubic-bezier(0.4,0,0.2,1);left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px`;
    document.body.appendChild(ws);

    // Render workspace content
    renderWorkspaceInner(ws, proj, cardColumn, opts);

    // Animate to final position
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ws.style.left = '50%';
        ws.style.transform = 'translateX(-50%)';
        ws.style.top = '6vh';
        ws.style.width = 'min(92vw, 720px)';
        ws.style.height = '88vh';
        scrim.style.opacity = '1';
      });
    });

    // Hide original card
    if (cardEl) cardEl.style.visibility = 'hidden';

    // Track state
    _activeWorkspace = { cardId, taskId: opts.taskId || null, el: ws, cardEl, scrim, proj };

    window.location.hash = '#workspace=' + cardId;
    document.body.classList.add('card-workspace-active');

    // Close handlers
    scrim.addEventListener('click', closeWorkspace);
    const escHandler = (ev) => {
      if (ev.key === 'Escape' && _activeWorkspace) { closeWorkspace(); document.removeEventListener('keydown', escHandler); }
    };
    document.addEventListener('keydown', escHandler);
    _activeWorkspace._escHandler = escHandler;
  }

export function closeWorkspace() {
    if (!_activeWorkspace) return;
    const { el, cardEl, scrim, _escHandler } = _activeWorkspace;
    if (el) el.remove();
    if (scrim) scrim.remove();
    if (cardEl) cardEl.style.visibility = '';
    if (_escHandler) document.removeEventListener('keydown', _escHandler);
    if (_workspacePollTimer) { clearInterval(_workspacePollTimer); _workspacePollTimer = null; }
    history.replaceState(null, '', window.location.pathname + window.location.search);
    document.body.classList.remove('card-workspace-active');
    // Close any open detail popup too
    const detailPopup = document.getElementById('cap-detail-popup');
    if (detailPopup) detailPopup.remove();
    const detailScrim = document.getElementById('cap-detail-scrim');
    if (detailScrim) detailScrim.remove();
    _activeWorkspace = null;
    renderFromState();
  }

export function refreshWorkspace() {
    if (!_activeWorkspace) return;
    const merged = mergeOverrides(boardData);
    const proj = findCardById(_activeWorkspace.cardId, merged);
    if (!proj) return;
    _activeWorkspace.proj = proj;
    const ws = _activeWorkspace.el;
    if (!ws) return;
    // Find column
    let cardColumn = '';
    for (const col of merged.columns) {
      for (const p of (col.projects || [])) {
        if (p.id === _activeWorkspace.cardId) { cardColumn = col.name; break; }
        if (p.children) { for (const c of p.children) { if (c.id === _activeWorkspace.cardId) { cardColumn = col.name; break; } } }
      }
    }
    renderWorkspaceInner(ws, proj, cardColumn, { taskId: _activeWorkspace.taskId });
  }

export function renderWorkspaceInner(ws, proj, cardColumn, opts) {
    const ov = loadOverrides();
    const status = normalizeStatus(ov.cardStatusOverrides?.[proj.id]?.status || ov.cardStatus?.[proj.id] || proj.status || 'Active');
    const dueDate = ov.cardDueDates?.[proj.id] || proj.dueDate || '';
    const endDate = ov.cardEndDates?.[proj.id] || proj.endDate || '';
    const statusStyle = getStatusStyle(status);
    const archived = ov.archivedCards && ov.archivedCards[proj.id];
    const tasks = proj.tasks || [];
    const hasIncompleteTasks = tasks.some(t => !t.done);

    // Find column ID for duplicate action
    let cardColumnId = '';
    const merged = mergeOverrides(boardData);
    for (const col of merged.columns) {
      for (const p of (col.projects || [])) {
        if (p.id === proj.id) { cardColumnId = col.id; break; }
        if (p.children) { for (const c of p.children) { if (c.id === proj.id) { cardColumnId = col.id; break; } } }
      }
    }

    ws.innerHTML = '';

    // ── Header ──
    const header = document.createElement('div');
    header.className = 'workspace-header';
    header.innerHTML = `
      <div class="workspace-header-left">
        <span class="workspace-card-name">${e(proj.name)}</span>
        ${cardColumn ? `<span class="workspace-col-label">${e(cardColumn)}</span>` : ''}
        <span class="workspace-status-chip" style="${statusStyle}">${e(status)}</span>
        ${dueDate ? `<span class="workspace-due">${formatShortDate(dueDate)}${endDate ? ' \u2013 ' + formatShortDate(endDate) : ''}</span>` : ''}
      </div>
      <div class="workspace-header-actions">
        ${hasIncompleteTasks ? `<button class="ws-header-focus-btn" title="Enter Focus mode">\ud83c\udfaf Focus</button>` : ''}
        <button class="ws-header-dup-btn" title="Duplicate card">\u2a77 Duplicate</button>
        <button class="ws-header-archive-btn" title="${archived ? 'Unarchive' : 'Archive'}">${archived ? '\ud83d\udcc2 Unarchive' : '\ud83d\udcc1 Archive'}</button>
        <button class="workspace-close-btn" title="Close workspace">\u2715</button>
      </div>
    `;
    ws.appendChild(header);
    header.querySelector('.workspace-close-btn').addEventListener('click', closeWorkspace);
    const hdrFocusBtn = header.querySelector('.ws-header-focus-btn');
    if (hdrFocusBtn) hdrFocusBtn.addEventListener('click', () => { enterWorkMode(proj.id); closeWorkspace(); });
    header.querySelector('.ws-header-dup-btn').addEventListener('click', () => { duplicateCard(proj, cardColumnId); closeWorkspace(); });
    header.querySelector('.ws-header-archive-btn').addEventListener('click', () => { toggleArchive(proj.id); closeWorkspace(); });

    // ── Body (single column) ──
    const body = document.createElement('div');
    body.className = 'workspace-body';
    renderWorkspaceBody(body, proj, ov, opts);
    ws.appendChild(body);
  }

export function renderWorkspaceBody(container, proj, ov, opts) {
    const outcome = ov.outcomeOverrides?.[proj.id] ?? proj.outcome ?? '';
    const waitingFor = ov.waitingForOverrides?.[proj.id] ?? proj.waitingFor ?? '';
    const contacts = proj.contacts || [];
    const tasks = proj.tasks || [];
    const hasIncompleteTasks = tasks.some(t => !t.done);
    const hist = ov.delegateHistory?.[proj.id] || [];

    // ── Pending Cap artifacts ──
    const cardArtifactKey = 'card:' + proj.id;
    const cardArtifacts = [];
    if (ov.delegateArtifacts) {
      const cardLevel = ov.delegateArtifacts[cardArtifactKey];
      if (cardLevel && !isArtifactSnoozed(cardArtifactKey)) cardArtifacts.push({ key: cardArtifactKey, artifact: cardLevel, taskText: 'Card-level', isCardLevel: true });
      for (const t of tasks) {
        const a = ov.delegateArtifacts[t.id];
        if (a && !isArtifactSnoozed(t.id)) cardArtifacts.push({ key: t.id, artifact: a, taskText: t.text, isCardLevel: false });
      }
    }

    let artifactHtml = '';
    for (const { key, artifact, taskText } of cardArtifacts) {
      const typeLabel = artifact.type === 'email_draft' ? '📧 Email draft' : artifact.type === 'research' ? '🔍 Research' : artifact.type === 'document' ? '📄 Draft' : '◈ Result';
      const isEmailDraft = artifact.type === 'email_draft';
      const fullContent = artifact.content || artifact.result?.body || artifact.result?.text || '';
      if (artifact.revising) {
        artifactHtml += `<div class="ws-artifact-block ws-artifact-revising" data-artifact-key="${e(key)}"><span class="artifact-revising-spin">↻</span> ${e(AI_NAME)} is revising…${artifact.revisingNote ? `<div class="artifact-revising-note">"${e(artifact.revisingNote)}"</div>` : ''}</div>`;
      } else {
        artifactHtml += `
          <div class="ws-artifact-block" data-artifact-key="${e(key)}">
            <div class="ws-artifact-type-row"><span class="ws-artifact-type">${typeLabel}</span>${cardArtifacts.length > 1 ? `<span class="ws-artifact-task-label">${e(taskText)}</span>` : ''}</div>
            <pre class="ws-artifact-content">${e(fullContent.slice(0, 400))}${fullContent.length > 400 ? '…' : ''}</pre>
            <div class="artifact-actions">
              <button class="delegate-btn-approve" data-artifact-task-id="${e(key)}" data-delegate-id="${e(artifact.delegateId||'')}" data-artifact-type="${e(artifact.type||'')}">${isEmailDraft ? '✓ Approve & Send' : '✓ Approve'}</button>
              <button class="delegate-btn-view" data-artifact-task-id="${e(key)}">View full</button>
              <button class="delegate-btn-edit" data-artifact-task-id="${e(key)}">Edit</button>
              <button class="delegate-btn-snooze" data-artifact-task-id="${e(key)}">⏰ Snooze</button>
              <button class="delegate-btn-discard" data-artifact-task-id="${e(key)}" data-delegate-id="${e(artifact.delegateId||'')}">Discard</button>
            </div>
          </div>`;
      }
    }

    container.innerHTML = `
      <div class="ws-section">
        <label class="ws-label">Outcome</label>
        <textarea class="ws-outcome-field" data-proj-id="${proj.id}" placeholder="What does done look like?">${e(outcome)}</textarea>
      </div>
      <div class="ws-section">
        <div class="ws-section-header">
          <label class="ws-label">Tasks</label>
          ${hasIncompleteTasks ? `<button class="ws-focus-btn">🎯 Focus on this</button>` : ''}
        </div>
        <ul class="ws-task-list" data-project-id="${proj.id}">
          ${tasks.map(t => {
            const { context: tCtx, displayText: tDisplay } = parseContext(t.text);
            const isDelegate = tCtx === 'delegate';
            const ctxPill = tCtx ? `<span class="task-ctx-pill${tCtx === 'waiting' ? ' task-ctx-waiting' : ''}${isDelegate ? ' task-ctx-delegate' : ''}" title="@${tCtx}">@${e(tCtx)}</span>` : '';
            return `<li class="ws-task-item ${t.done ? 'done' : ''}${isDelegate ? ' task-is-delegate' : ''}" data-task-id="${t.id}">
              <input type="checkbox" ${t.done ? 'checked' : ''} data-task-id="${t.id}">
              ${t.ripe ? '<span class="task-ripe-indicator" title="Cue">\ud83d\udd37</span>' : ''}
              <span class="ws-task-text" data-task-id="${t.id}" title="Click to edit">${e(tDisplay)}</span>
              ${ctxPill}
              <button class="ws-task-delete" data-task-id="${t.id}" title="Delete">×</button>
            </li>`;
          }).join('')}
        </ul>
        <div class="ws-add-task-row">
          <textarea class="ws-add-task-input" placeholder="Next physical step\u2026" data-project-id="${proj.id}" rows="1"></textarea>
          <button class="ws-ripe-toggle ripe-toggle" data-project-id="${proj.id}" title="Mark as Cue" style="opacity:0.3">\ud83d\udd37</button>
          <button class="ws-add-task-btn" data-project-id="${proj.id}">Add</button>
        </div>
      </div>
      <div class="ws-section">
        <label class="ws-label">Waiting on</label>
        <textarea class="ws-waiting-field" data-proj-id="${proj.id}" placeholder="Who or what are you waiting on?">${e(waitingFor)}</textarea>
      </div>
      <div class="ws-section">
        <label class="ws-label">Contacts</label>
        <div class="ws-contacts-list">
          ${contacts.length === 0 ? '<div class="ws-empty">None yet</div>' : contacts.map(c => `<div class="ws-contact"><span class="ws-contact-name">${e(c.name)}</span><span class="ws-contact-email">${e(c.email)}</span></div>`).join('')}
        </div>
        <div class="ws-add-contact-row">
          <input class="ws-contact-name-input" placeholder="Name">
          <input class="ws-contact-email-input" placeholder="Email">
          <button class="ws-add-contact-btn">Add</button>
        </div>
      </div>
      <div class="ws-section ws-cap-section">
        <label class="ws-label">Ask Cap</label>
        ${artifactHtml}
        ${cardArtifacts.length === 0 ? `
        <textarea class="ws-delegate-input" placeholder="What do you need Cap to do?" rows="3"></textarea>
        <button class="ws-delegate-btn">Ask Cap →</button>` : ''}
        ${hist.length ? `
        <div class="ws-history-section">
          <button class="ws-history-toggle">Cap history <span class="ws-history-count">(${hist.length})</span></button>
          <div class="ws-history-list hidden">${hist.slice().reverse().map(h => {
            const icon = h.action === 'approved' ? '✓' : '✗';
            const label = h.action === 'approved' ? 'Approved' : 'Discarded';
            const typeLabel = h.type === 'email_draft' ? 'email draft' : h.type || 'result';
            const d = new Date(h.at);
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const timeStr = months[d.getMonth()] + ' ' + d.getDate() + ' ' + d.getHours() + ':' + String(d.getMinutes()).padStart(2,'0');
            return `<div class="ws-history-entry ws-history-${h.action}"><span>${icon}</span> ${label} ${e(typeLabel)} — ${timeStr}</div>`;
          }).join('')}</div>
        </div>` : ''}
      </div>
    `;

    // Wire outcome
    const outcomeField = container.querySelector('.ws-outcome-field');
    if (outcomeField) outcomeField.addEventListener('blur', () => { const ov2 = loadOverrides(); if (!ov2.outcomeOverrides) ov2.outcomeOverrides = {}; ov2.outcomeOverrides[proj.id] = outcomeField.value; saveOverrides(ov2); });

    // Wire waiting-on
    const waitingField = container.querySelector('.ws-waiting-field');
    if (waitingField) waitingField.addEventListener('blur', () => { const ov2 = loadOverrides(); if (!ov2.waitingForOverrides) ov2.waitingForOverrides = {}; ov2.waitingForOverrides[proj.id] = waitingField.value; saveOverrides(ov2); });

    // Wire focus btn
    const focusBtn = container.querySelector('.ws-focus-btn');
    if (focusBtn) focusBtn.addEventListener('click', () => { closeWorkspace(); enterWorkMode(proj.id); });

    // Wire task checkboxes
    container.querySelectorAll('.ws-task-item input[type="checkbox"]').forEach(cb => {
      cb.addEventListener('change', () => {
        const taskId = cb.dataset.taskId;
        const ov2 = loadOverrides();
        if (!ov2.taskDoneOverrides) ov2.taskDoneOverrides = {};
        if (cb.checked) { ov2.taskDoneOverrides[taskId] = { done: true, doneAt: new Date().toISOString() }; }
        else { delete ov2.taskDoneOverrides[taskId]; }
        saveOverrides(ov2);
        refreshWorkspace();
      });
    });

    // Wire task delete
    container.querySelectorAll('.ws-task-delete').forEach(btn => {
      btn.addEventListener('click', () => {
        const taskId = btn.dataset.taskId;
        const ov2 = loadOverrides();
        if (!ov2.deletedTasks) ov2.deletedTasks = new Set ? {} : {};
        ov2.deletedTasks[taskId] = true;
        saveOverrides(ov2);
        refreshWorkspace();
      });
    });

    // Wire task text edit
    container.querySelectorAll('.ws-task-text').forEach(span => {
      span.addEventListener('click', () => {
        const taskId = span.dataset.taskId;
        const current = span.textContent;
        const input = document.createElement('input');
        input.value = current; input.className = 'ws-task-edit-input';
        span.replaceWith(input); input.focus(); input.select();
        const save = () => {
          const newText = input.value.trim();
          if (newText && newText !== current) {
            const ov2 = loadOverrides();
            if (!ov2.taskTextOverrides) ov2.taskTextOverrides = {};
            ov2.taskTextOverrides[taskId] = newText;
            saveOverrides(ov2);
          }
          refreshWorkspace();
        };
        input.addEventListener('blur', save);
        input.addEventListener('keydown', ev => { if (ev.key === 'Enter') { ev.preventDefault(); save(); } if (ev.key === 'Escape') refreshWorkspace(); });
      });
    });

    // Wire add task
    const addTaskInput = container.querySelector('.ws-add-task-input');
    const addTaskBtn = container.querySelector('.ws-add-task-btn');
    const wsRipeToggle = container.querySelector('.ws-ripe-toggle');
    let _wsAddTaskRipe = false;
    if (wsRipeToggle) {
      wsRipeToggle.addEventListener('click', () => {
        _wsAddTaskRipe = !_wsAddTaskRipe;
        wsRipeToggle.style.opacity = _wsAddTaskRipe ? '1' : '0.3';
      });
    }
    if (addTaskBtn && addTaskInput) {
      const doAdd = () => {
        const text = addTaskInput.value.trim();
        if (!text) return;
        const hasCueTrigger = _wsAddTaskRipe || /@(?:quick|cue)\b/i.test(text) || /\bquick\b/i.test(text);
        const cleanText = text.replace(/@(?:quick|cue)\b/gi, '').replace(/\bquick\b/gi, '').replace(/\s{2,}/g, ' ').trim();
        const taskId = proj.id + '-task-' + Date.now();
        const ov2 = loadOverrides();
        if (!ov2.addedTasks) ov2.addedTasks = {};
        if (!ov2.addedTasks[proj.id]) ov2.addedTasks[proj.id] = [];
        ov2.addedTasks[proj.id].push({ id: taskId, text: cleanText, done: false });
        if (hasCueTrigger) {
          if (!ov2.taskRipe) ov2.taskRipe = {};
          ov2.taskRipe[taskId] = true;
          _wsAddTaskRipe = false;
          if (wsRipeToggle) wsRipeToggle.style.opacity = '0.3';
        }
        saveOverrides(ov2);
        if (/@delegate\b/i.test(text)) {
          backgroundFetch('/api/v1/delegate', { method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ cardId: proj.id, cardName: proj.name || '', taskId, taskText: text, cardScope: proj.outcome || '', cardNotes: '' }) });
        }
        refreshWorkspace();
      };
      addTaskBtn.addEventListener('click', doAdd);
      addTaskInput.addEventListener('keydown', ev => { if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); doAdd(); } });
    }

    // Wire add contact
    const addContactBtn = container.querySelector('.ws-add-contact-btn');
    if (addContactBtn) {
      addContactBtn.addEventListener('click', () => {
        const nameIn = container.querySelector('.ws-contact-name-input');
        const emailIn = container.querySelector('.ws-contact-email-input');
        const name = nameIn?.value.trim(); const email = emailIn?.value.trim();
        if (!name) return;
        const ov2 = loadOverrides();
        if (!ov2.contactOverrides) ov2.contactOverrides = {};
        if (!ov2.contactOverrides[proj.id]) ov2.contactOverrides[proj.id] = [...contacts];
        ov2.contactOverrides[proj.id].push({ name, email: email || '' });
        saveOverrides(ov2);
        refreshWorkspace();
      });
    }

    // Wire Ask Cap delegate btn
    const delegateBtn = container.querySelector('.ws-delegate-btn');
    const delegateInput = container.querySelector('.ws-delegate-input');
    if (delegateBtn && delegateInput) {
      delegateBtn.addEventListener('click', async () => {
        let text = delegateInput.value.trim();
        if (!text) return;
        if (!/@delegate\b/i.test(text)) text += ' @delegate';
        const taskId = proj.id + '-custom-' + Date.now();
        const ov2 = loadOverrides();
        if (!ov2.addedTasks) ov2.addedTasks = {};
        if (!ov2.addedTasks[proj.id]) ov2.addedTasks[proj.id] = [];
        ov2.addedTasks[proj.id].push({ id: taskId, text, done: false });
        saveOverrides(ov2);
        try {
          await fetch('/api/v1/delegate', { method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ cardId: proj.id, cardName: proj.name || '', taskId, taskText: text, cardScope: proj.outcome || '', cardNotes: '' }) });
        } catch (err) { console.warn('[delegate] enqueue failed:', err.message); }
        container.querySelector('.ws-cap-section').innerHTML = `<div class="ws-revising"><div class="ws-revising-icon">◈</div><div class="ws-revising-text">${e(AI_NAME)} is on it…</div></div>`;
        let pollCount = 0;
        if (_workspacePollTimer) clearInterval(_workspacePollTimer);
        _workspacePollTimer = setInterval(async () => {
          pollCount++;
          if (pollCount > 24) { clearInterval(_workspacePollTimer); _workspacePollTimer = null; refreshWorkspace(); return; }
          try { await refreshDelegateQueue(); const ov3 = loadOverrides(); if (ov3.delegateArtifacts?.[taskId]) { clearInterval(_workspacePollTimer); _workspacePollTimer = null; refreshWorkspace(); } } catch { }
        }, 5000);
      });
    }

    // Wire artifact approve/discard/view/snooze/edit buttons (reuse existing handlers via event delegation)
    container.querySelectorAll('.delegate-btn-approve').forEach(btn => {
      btn.addEventListener('click', e => { e.stopPropagation(); const key = btn.dataset.artifactTaskId; const did = btn.dataset.delegateId; const type = btn.dataset.artifactType; const isCardLevel = key?.startsWith('card:'); wsPerformApprove(key, isCardLevel, did); });
    });
    container.querySelectorAll('.delegate-btn-discard').forEach(btn => {
      btn.addEventListener('click', e => { e.stopPropagation(); const key = btn.dataset.artifactTaskId; const did = btn.dataset.delegateId; const ov2 = loadOverrides(); const snap = ov2.delegateArtifacts?.[key]; if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[key]; saveOverrides(ov2); showDiscardUndoToast(key, did, snap); refreshWorkspace(); });
    });
    container.querySelectorAll('.delegate-btn-snooze').forEach(btn => {
      btn.addEventListener('click', e => { e.stopPropagation(); const key = btn.dataset.artifactTaskId; snoozeArtifact(key); refreshWorkspace(); });
    });
    container.querySelectorAll('.delegate-btn-view').forEach(btn => {
      btn.addEventListener('click', e => { e.stopPropagation(); openWorkspace(proj.id, { taskId: btn.dataset.artifactTaskId }); });
    });

    // Wire Cap history toggle
    const histToggle = container.querySelector('.ws-history-toggle');
    const histList = container.querySelector('.ws-history-list');
    if (histToggle && histList) histToggle.addEventListener('click', () => histList.classList.toggle('hidden'));

    // Scroll to artifact if specified
    if (opts?.taskId) setTimeout(() => { const t = container.querySelector(`.ws-artifact-block[data-artifact-key="${opts.taskId}"]`); if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 300);
  }

export function renderWorkspaceRight(container, proj, ov, opts) {
    const artifacts = ov.delegateArtifacts || {};
    const tasks = proj.tasks || [];

    // Collect artifacts for this card
    const cardArtifacts = [];
    // Card-level artifact
    const cardKey = 'card:' + proj.id;
    if (artifacts[cardKey]) cardArtifacts.push({ key: cardKey, artifact: artifacts[cardKey], taskText: proj.name, isCardLevel: true });
    // Task-level artifacts
    tasks.forEach(t => {
      if (artifacts[t.id]) cardArtifacts.push({ key: t.id, artifact: artifacts[t.id], taskText: t.text, isCardLevel: false });
    });

    if (cardArtifacts.length > 0) {
      renderWorkspaceRightArtifacts(container, proj, cardArtifacts, ov, opts);
    } else {
      renderWorkspaceRightEmpty(container, proj, ov);
    }
  }

export function renderWorkspaceRightArtifacts(container, proj, cardArtifacts, ov, opts) {
    let html = '';
    for (const { key, artifact, taskText, isCardLevel } of cardArtifacts) {
      const typeLabel = artifact.type === 'email_draft' ? '\ud83d\udce7 Email draft' : artifact.type === 'research' ? '\ud83d\udd0d Research' : artifact.type === 'document' ? '\ud83d\udcc4 Document' : '\u25c8 Result';
      const isEmailDraft = artifact.type === 'email_draft';
      const confHtml = artifact.confidence ? `<span class="artifact-confidence artifact-confidence-${artifact.confidence}">${confidenceGlyph(artifact.confidence)} ${e(artifact.confidence)}</span>` : '';
      const provHtml = artifact.provenance ? `<div class="ws-provenance">Source: ${e(artifact.provenance)}</div>` : '';
      const fullContent = artifact.content || artifact.result?.body || artifact.result?.text || '';
      const reqText = artifact.taskText || taskText || '';

      html += `
        <div class="ws-artifact-block" data-artifact-key="${e(key)}">
          ${cardArtifacts.length > 1 ? `<div class="ws-artifact-task-label">Task: ${e(taskText)}</div>` : ''}
          <div class="ws-artifact-type-row">
            <span class="ws-artifact-type">${typeLabel}</span>
            ${confHtml}
          </div>
          ${provHtml}
          <div class="ws-artifact-section-label">\u2500 What was requested</div>
          <div class="ws-artifact-request">${e(reqText)}</div>
          <div class="ws-artifact-section-label">\u2500 ${e(AI_NAME)}'s response</div>
          <div class="ws-artifact-content-scroll">
            ${artifact.to ? `<div class="ws-artifact-meta"><strong>To:</strong> ${e(artifact.to)}</div>` : ''}
            ${artifact.subject ? `<div class="ws-artifact-meta"><strong>Subject:</strong> ${e(artifact.subject)}</div>` : ''}
            <pre class="ws-artifact-content">${e(fullContent)}</pre>
          </div>
          <div class="ws-artifact-section-label">\u2500 Request changes</div>
          <div class="ws-feedback-area">
            <textarea class="ws-feedback-input" data-key="${e(key)}" placeholder="Tell ${e(AI_NAME)} what to change, add, or fix\u2026" rows="3"></textarea>
            <button class="ws-feedback-send" data-key="${e(key)}">Send to ${e(AI_NAME)}</button>
          </div>
          <div class="ws-artifact-actions">
            <button class="ws-approve-btn" data-key="${e(key)}" data-delegate-id="${e(artifact.delegateId || '')}" data-artifact-type="${e(artifact.type || '')}">${isEmailDraft ? '\u2713 Approve & Send' : '\u2713 Approve'}</button>
            <button class="ws-discard-btn" data-key="${e(key)}" data-delegate-id="${e(artifact.delegateId || '')}">✗ Discard</button>
          </div>
        </div>
      `;
    }
    container.innerHTML = `<div class="ws-right-scroll">${html}</div>`;

    // Wire approve buttons
    container.querySelectorAll('.ws-approve-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        const delegateId = btn.dataset.delegateId;
        const artifactType = btn.dataset.artifactType;
        const isCardLevel = key.startsWith('card:');
        const ov2 = loadOverrides();
        const artifact = ov2.delegateArtifacts?.[key];
        if (artifactType === 'email_draft' && artifact) {
          showSendConfirmModal(artifact, () => {
            wsPerformApprove(key, isCardLevel, delegateId);
          });
          return;
        }
        wsPerformApprove(key, isCardLevel, delegateId);
      });
    });

    // Wire discard buttons
    container.querySelectorAll('.ws-discard-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        const delegateId = btn.dataset.delegateId;
        const ov2 = loadOverrides();
        const artifact = ov2.delegateArtifacts?.[key];
        const snap = artifact ? JSON.parse(JSON.stringify(artifact)) : null;
        const ov3 = loadOverrides();
        if (ov3.delegateArtifacts) delete ov3.delegateArtifacts[key];
        saveOverrides(ov3);
        showDiscardUndoToast(key, delegateId, snap);
        refreshWorkspace();
      });
    });

    // Wire feedback send
    container.querySelectorAll('.ws-feedback-send').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const textarea = container.querySelector(`.ws-feedback-input[data-key="${key}"]`);
        const feedbackText = textarea ? textarea.value.trim() : '';
        if (!feedbackText) return;
        const ov2 = loadOverrides();
        const artifact = ov2.delegateArtifacts?.[key];
        if (!artifact) return;
        const isCardLevel = key.startsWith('card:');
        const cardId = isCardLevel ? key.slice(5) : proj.id;
        const cardName = proj.name || '';
        const originalTask = artifact.taskText || (isCardLevel ? `Review: ${cardName}` : key);
        const revisedTask = `${originalTask} @delegate`;
        const feedbackNote = `[Revision request: ${feedbackText}]\nOriginal result:\n${(artifact.content || '').slice(0, 500)}`;
        // Mark as revising (optimistic) instead of deleting — cleared when new result arrives
        if (!ov2.delegateArtifacts) ov2.delegateArtifacts = {};
        ov2.delegateArtifacts[key] = { ...artifact, revising: true, revisingNote: feedbackText };
        saveOverrides(ov2);
        // Re-enqueue
        try {
          await fetch('/api/v1/delegate', {
            method: 'POST',
            headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ cardId, cardName, taskId: key, taskText: revisedTask, cardScope: proj.outcome || proj.scope || '', cardNotes: feedbackNote }),
          });
        } catch (err) { console.warn('[delegate] feedback re-queue failed:', err); }
        // Show revising state in workspace panel
        container.innerHTML = `<div class="ws-revising"><div class="ws-revising-icon">\u23f3</div><div class="ws-revising-text">${e(AI_NAME)} is revising\u2026</div></div>`;
        // Poll for new artifact
        let pollCount = 0;
        if (_workspacePollTimer) clearInterval(_workspacePollTimer);
        _workspacePollTimer = setInterval(async () => {
          pollCount++;
          if (pollCount > 24) { clearInterval(_workspacePollTimer); _workspacePollTimer = null; refreshWorkspace(); return; }
          try {
            await refreshDelegateQueue();
            const ov3 = loadOverrides();
            if (ov3.delegateArtifacts?.[key]) { clearInterval(_workspacePollTimer); _workspacePollTimer = null; refreshWorkspace(); }
          } catch { /* retry */ }
        }, 5000);
      });
    });

    // Scroll to target artifact if specified
    if (opts && opts.taskId) {
      setTimeout(() => {
        const target = container.querySelector(`.ws-artifact-block[data-artifact-key="${opts.taskId}"]`);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }

export function renderWorkspaceRightEmpty(container, proj, ov) {
    const hist = ov.delegateHistory?.[proj.id] || [];
    container.innerHTML = `
      <div class="ws-right-scroll">
        <div class="ws-empty-state">
          <div class="ws-empty-icon">\u25c8</div>
          <div class="ws-empty-title">Hand off to ${e(AI_NAME)}</div>
          <div class="ws-empty-subtitle">What do you need ${e(AI_NAME)} to research, draft, or find?</div>
          <textarea class="ws-delegate-input" placeholder="Describe what you need\u2026" rows="3"></textarea>
          <button class="ws-delegate-btn">Delegate to ${e(AI_NAME)} \u2192</button>
          <div class="ws-empty-caps">
            <div class="ws-empty-cap-title">${e(AI_NAME)} can:</div>
            <ul>
              <li>Search your email for relevant threads</li>
              <li>Draft emails for your review</li>
              <li>Research contacts, vendors, or deadlines</li>
            </ul>
          </div>
          ${hist.length ? `<div class="ws-history-inline">
            <div class="ws-history-inline-title">${e(AI_NAME)} history for this card</div>
            ${hist.slice().reverse().slice(0, 5).map(h => {
              const icon = h.action === 'approved' ? '\u2713' : '\u2717';
              const label = h.action === 'approved' ? 'Approved' : 'Discarded';
              const typeLabel = h.type === 'email_draft' ? 'email draft' : h.type || 'result';
              const d = new Date(h.at);
              const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              const timeStr = months[d.getMonth()] + ' ' + d.getDate() + ' ' + d.getHours() + ':' + String(d.getMinutes()).padStart(2,'0');
              return `<div class="ws-history-entry ws-history-${h.action}"><span>${icon}</span> ${label} ${e(typeLabel)} \u2014 ${timeStr}</div>`;
            }).join('')}
          </div>` : ''}
        </div>
      </div>
    `;

    // Wire delegate button
    const delegateBtn = container.querySelector('.ws-delegate-btn');
    const delegateInput = container.querySelector('.ws-delegate-input');
    if (delegateBtn && delegateInput) {
      delegateBtn.addEventListener('click', async () => {
        let text = delegateInput.value.trim();
        if (!text) return;
        if (!/@delegate\b/i.test(text)) text += ' @delegate';
        const taskId = proj.id + '-custom-' + Date.now();
        const ov2 = loadOverrides();
        if (!ov2.addedTasks) ov2.addedTasks = {};
        if (!ov2.addedTasks[proj.id]) ov2.addedTasks[proj.id] = [];
        ov2.addedTasks[proj.id].push({ id: taskId, text, done: false });
        saveOverrides(ov2);
        // Enqueue
        try {
          await fetch('/api/v1/delegate', {
            method: 'POST',
            headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ cardId: proj.id, cardName: proj.name || '', taskId, taskText: text, cardScope: proj.outcome || proj.scope || '', cardNotes: (proj.comms || []).map(c => c.text).join('\n') }),
          });
        } catch (err) { console.warn('[delegate] enqueue failed:', err.message); }
        // Show waiting state
        container.innerHTML = `<div class="ws-revising"><div class="ws-revising-icon">\u25c8</div><div class="ws-revising-text">${e(AI_NAME)} is on it\u2026</div></div>`;
        // Poll
        let pollCount = 0;
        if (_workspacePollTimer) clearInterval(_workspacePollTimer);
        _workspacePollTimer = setInterval(async () => {
          pollCount++;
          if (pollCount > 24) { clearInterval(_workspacePollTimer); _workspacePollTimer = null; refreshWorkspace(); return; }
          try {
            await refreshDelegateQueue();
            const ov3 = loadOverrides();
            if (ov3.delegateArtifacts?.[taskId]) { clearInterval(_workspacePollTimer); _workspacePollTimer = null; refreshWorkspace(); }
          } catch { /* retry */ }
        }, 5000);
      });
    }
  }

export function wsPerformApprove(taskId, isCardLevel, delegateId) {
    // Delegate history
    const _artOv = loadOverrides();
    const _art = _artOv.delegateArtifacts?.[taskId];
    if (_art) {
      const _cardId = isCardLevel ? taskId.slice(5) : (_activeWorkspace ? _activeWorkspace.cardId : '');
      writeDelegateHistory(taskId, _cardId, _art.type || 'result', 'approved');
    }
    if (delegateId) {
      const hdrs = getAuthHeader();
      hdrs['Content-Type'] = 'application/json';
      fetch(`/api/v1/delegate/approve/${delegateId}`, { method: 'POST', headers: hdrs, body: JSON.stringify({}) })
        .then(r => r.json()).then(res => {
          const ov2 = loadOverrides();
          if (res.status !== 'approved_pending_send') {
            if (isCardLevel) {
              const cardId = taskId.slice(5);
              if (!ov2.cardStatusOverrides) ov2.cardStatusOverrides = {};
              ov2.cardStatusOverrides[cardId] = { status: 'Done', doneAt: new Date().toISOString() };
              if (!ov2.cardStatus) ov2.cardStatus = {};
              ov2.cardStatus[cardId] = 'Done';
            } else {
              if (!ov2.taskStates) ov2.taskStates = {};
              ov2.taskStates[taskId] = true;
              ov2.taskCompletedAt = ov2.taskCompletedAt || {};
              ov2.taskCompletedAt[taskId] = new Date().toISOString();
              if (!ov2.taskDoneOverrides) ov2.taskDoneOverrides = {};
              ov2.taskDoneOverrides[taskId] = { done: true, doneAt: new Date().toISOString() };
            }
          }
          if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[taskId];
          saveOverrides(ov2);
          refreshWorkspace();
        }).catch(err => console.warn('[delegate] ws approve failed:', err.message));
    } else {
      const ov2 = loadOverrides();
      if (isCardLevel) {
        const cardId = taskId.slice(5);
        if (!ov2.cardStatusOverrides) ov2.cardStatusOverrides = {};
        ov2.cardStatusOverrides[cardId] = { status: 'Done', doneAt: new Date().toISOString() };
        if (!ov2.cardStatus) ov2.cardStatus = {};
        ov2.cardStatus[cardId] = 'Done';
      } else {
        if (!ov2.taskStates) ov2.taskStates = {};
        ov2.taskStates[taskId] = true;
        ov2.taskCompletedAt = ov2.taskCompletedAt || {};
        ov2.taskCompletedAt[taskId] = new Date().toISOString();
        if (!ov2.taskDoneOverrides) ov2.taskDoneOverrides = {};
        ov2.taskDoneOverrides[taskId] = { done: true, doneAt: new Date().toISOString() };
      }
      if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[taskId];
      saveOverrides(ov2);
      refreshWorkspace();
    }
  }

// ── Toolbar active-state sync ──

export function syncToolbarActive() {
    document.getElementById('next-actions-btn').classList.toggle('active', !!_focusMode);
    document.getElementById('review-btn').classList.toggle('active', !!_reviewMode);
    // wins-btn removed from toolbar; badge now lives on review-btn
    const intActive = typeof _intentionsActive !== 'undefined' && _intentionsActive;
    document.getElementById('intentions-btn').classList.toggle('active', intActive);
  }

// ── Day 10: Wins panel ──────────────────────────────────────────────────────

export function setWinsMode(on) {
    _winsMode = on;
    _reviewMode = on ? true : _reviewMode; // wins lives inside review panel
    document.getElementById('board').classList.toggle('hidden', on);
    document.getElementById('review-view').classList.toggle('hidden', !on);
    document.getElementById('focus-view').classList.add('hidden');
    document.getElementById('breadcrumb').classList.toggle('hidden', on || navStack.length === 0);
    if (on) {
      _focusMode = false;
      showReviewTab('wins');
      renderWins();
    }
    syncToolbarActive();
  }

export function showReviewTab(tab) {
    const isWins = tab === 'wins';
    document.getElementById('wins-view').classList.toggle('hidden', !isWins);
    document.getElementById('review-tab-content').classList.toggle('hidden', isWins);
    document.getElementById('tab-wins').classList.toggle('review-tab-active', isWins);
    document.getElementById('tab-review').classList.toggle('review-tab-active', !isWins);
  }

export function renderWins() {
    const ov = loadOverrides();
    const allWins = (ov.completedTasks || []).slice().reverse();
    const list = document.getElementById('wins-list');

    // Update badge
    const now = Date.now();
    const weekAgo = now - 7 * 24 * 60 * 60 * 1000;
    const weekCount = allWins.filter(w => new Date(w.completedAt).getTime() >= weekAgo).length;
    const badge = document.getElementById('wins-badge');
    if (weekCount > 0) {
      badge.textContent = weekCount;
      badge.classList.remove('hidden');
    } else {
      badge.classList.add('hidden');
    }

    const wins = allWins.slice(0, 30);
    if (wins.length === 0) {
      list.innerHTML = '<div class="wins-empty">No wins yet. Complete a task and it shows up here.</div>';
      return;
    }

    // Group by time bucket: Today / Yesterday / This week / Earlier
    const todayStart = new Date(); todayStart.setHours(0,0,0,0);
    const yesterdayStart = new Date(todayStart.getTime() - 86400000);
    const weekStart = new Date(todayStart.getTime() - 6 * 86400000);
    function timeBucket(iso) {
      const t = new Date(iso).getTime();
      if (t >= todayStart.getTime()) return 'Today';
      if (t >= yesterdayStart.getTime()) return 'Yesterday';
      if (t >= weekStart.getTime()) return 'This week';
      return 'Earlier';
    }

    const buckets = { 'Today': [], 'Yesterday': [], 'This week': [], 'Earlier': [] };
    for (const w of wins) buckets[timeBucket(w.completedAt)].push(w);

    let html = `<div class="wins-week-total">This week: <strong>${weekCount}</strong> task${weekCount !== 1 ? 's' : ''} done</div>`;
    for (const [label, items] of Object.entries(buckets)) {
      if (items.length === 0) continue;
      html += `<div class="wins-group"><div class="wins-group-label">${label}</div>`;
      for (const w of items) {
        html += `<div class="wins-item"><span class="wins-item-card">${e(w.cardName || '')}</span><span class="wins-item-text">${e(w.text)}</span><span class="wins-item-time">${formatRelativeTime(w.completedAt)}</span></div>`;
      }
      html += `</div>`;
    }
    list.innerHTML = html;
  }

// ── Day 13: Weekly Review Mode ──────────────────────────────────────────────

export function setReviewMode(on) {
    _reviewMode = on;
    _reviewStep = 0;
    _reviewCards = null;
    _reviewCardIdx = 0;
    document.getElementById('board').classList.toggle('hidden', on);
    document.getElementById('review-view').classList.toggle('hidden', !on);
    document.getElementById('focus-view').classList.add('hidden');
    document.getElementById('wins-view').classList.add('hidden');
    document.getElementById('breadcrumb').classList.toggle('hidden', on || navStack.length === 0);
    if (on) {
      _focusMode = false; _winsMode = false;
      showReviewTab('review');
      renderReviewStep();
    }
    syncToolbarActive();
  }

export function renderReviewStep() {
    const ov = loadOverrides();
    const data = mergeOverrides(boardData);
    const fill = document.getElementById('review-progress-fill');
    const content = document.getElementById('review-step-content');
    fill.style.width = `${((_reviewStep + 1) / 5) * 100}%`;

    if (_reviewStep === 0) {
      // Step 1 — Clear the decks
      const inboxCol = data.columns.find(c => /inbox|stash/i.test(c.name));
      const inboxCount = inboxCol ? inboxCol.projects.filter(p => !(ov.archivedCards && ov.archivedCards[p.id])).length : 0;
      content.innerHTML = `
        <div class="review-step-label">Step 1 of 5</div>
        <h2 class="review-step-title">Clear the decks</h2>
        <p class="review-step-prompt">${inboxCount > 0 ? `You have <strong>${inboxCount}</strong> item${inboxCount !== 1 ? 's' : ''} in your inbox. Process each one — move, clarify, or delete.` : 'Your inbox is clear. Nice work!'}</p>
        <button class="review-next-btn" id="review-next">Inbox is clear \u2192</button>
      `;
    } else if (_reviewStep === 1) {
      // Step 2 — Card-by-card review
      // Build card list on first entry to this step
      if (!_reviewCards) {
        _reviewCards = [];
        function gatherReviewCards(cards, colName) {
          for (const card of cards) {
            if (card.archived || (ov.archivedCards && ov.archivedCards[card.id])) continue;
            if (card.status !== 'Done' && card.status !== 'On Hold') {
              _reviewCards.push({ card, colName });
            }
            if (card.children?.length > 0) gatherReviewCards(card.children, colName);
          }
        }
        for (const col of data.columns) {
          if (ov.archivedColumns?.includes(col.id)) continue;
          gatherReviewCards(col.projects, col.name);
        }
        _reviewCardIdx = 0;
      }

      // If no active cards, skip to step 2
      if (_reviewCards.length === 0) { _reviewStep = 2; _reviewCards = null; renderReviewStep(); return; }
      // If all cards reviewed, advance
      if (_reviewCardIdx >= _reviewCards.length) { _reviewStep = 2; _reviewCards = null; renderReviewStep(); return; }

      const { card, colName } = _reviewCards[_reviewCardIdx];
      const undone = (card.tasks || []).filter(t => !t.done);
      const nextAction = undone.length > 0 ? parseContext(undone[0].text).displayText : null;
      const lastReview = ov.cardLastReview?.[card.id];
      const lastReviewText = lastReview
        ? `Reviewed ${formatRelativeTime(lastReview)}`
        : 'Never reviewed';
      const scope = card.outcome || '';
      const status = normalizeStatus(card.status || 'Active');
      const pct = Math.round((_reviewCardIdx / _reviewCards.length) * 100);

      let html = `<div class="review-step-label">Step 2 of 5 — Card ${_reviewCardIdx + 1} of ${_reviewCards.length}</div>`;
      html += `<div class="review-progress-sub"><div class="review-progress-sub-fill" style="width:${pct}%"></div></div>`;
      html += `<div class="review-card-block">`;
      html += `<div class="review-card-col-tag">${e(colName)}</div>`;
      html += `<h2 class="review-step-title">${e(card.name)}</h2>`;
      if (scope) html += `<p class="review-card-scope">${e(scope)}</p>`;
      html += `<div class="review-card-meta">`;
      html += `<span class="status-chip status-${status.toLowerCase().replace(' ', '-')}">${e(status)}</span>`;
      if (card.dueDate) {
        const urg = getDueDateUrgency(card.dueDate);
        html += `<span class="na-due-pill urgency-${urg} review-due-pill">${urg === 'overdue' ? 'Overdue' : formatShortDate(card.dueDate)}</span>`;
      }
      html += `<span class="review-last-review-tag">${e(lastReviewText)}</span>`;
      html += `</div>`;
      if (nextAction) {
        html += `<div class="review-card-na">→ ${e(nextAction)}</div>`;
      } else {
        html += `<div class="review-card-na review-card-na-empty">No next action — add one after review</div>`;
      }
      html += `</div>`;
      html += `<div class="review-card-btns">`;
      if (_reviewCardIdx > 0) html += `<button class="review-prev-card-btn" id="review-card-prev">← Back</button>`;
      html += `<button class="review-next-btn" id="review-next">Reviewed →</button>`;
      html += `</div>`;
      content.innerHTML = html;

      document.getElementById('review-next').addEventListener('click', () => {
        const ov2 = loadOverrides();
        if (!ov2.cardLastReview) ov2.cardLastReview = {};
        ov2.cardLastReview[card.id] = new Date().toISOString();
        saveOverrides(ov2);
        _reviewCardIdx++;
        renderReviewStep();
      });
      if (_reviewCardIdx > 0) {
        document.getElementById('review-card-prev').addEventListener('click', () => {
          _reviewCardIdx--;
          renderReviewStep();
        });
      }
      return; // skip the bottom review-next listener
    } else if (_reviewStep === 2) {
      // Step 3 — Check Waiting For
      let html = '<div class="review-step-label">Step 3 of 5</div><h2 class="review-step-title">Check Waiting For</h2>';
      html += '<p class="review-step-prompt">Is there anything you need to follow up on?</p>';
      html += '<div class="review-waiting-list">';
      function findWaiting(cards) {
        const items = [];
        for (const card of cards) {
          const wf = (ov.cardWaitingFor && ov.cardWaitingFor[card.id]) || '';
          if (wf.trim()) items.push({ name: card.name, text: wf.trim() });
          for (const t of (card.tasks || [])) {
            if (!t.done && parseContext(t.text).context === 'waiting') {
              items.push({ name: card.name, text: parseContext(t.text).displayText });
            }
            if (!t.done && t.owner) {
              items.push({ name: card.name, text: 'Assigned to ' + t.owner + ': ' + t.text });
            }
          }
          if (card.children) items.push(...findWaiting(card.children));
        }
        return items;
      }
      const waitingItems = [];
      for (const col of data.columns) waitingItems.push(...findWaiting(col.projects));
      if (waitingItems.length === 0) {
        html += '<div class="review-empty">Nothing in Waiting For.</div>';
      } else {
        for (const w of waitingItems) {
          html += `<div class="review-waiting-row"><span class="review-waiting-card">${e(w.name)}</span><span class="review-waiting-text">${e(w.text)}</span></div>`;
        }
      }
      html += '</div>';
      html += '<button class="review-next-btn" id="review-next">Waiting For reviewed \u2192</button>';
      content.innerHTML = html;
    } else if (_reviewStep === 3) {
      // Step 4 — Check Next Actions
      let html = '<div class="review-step-label">Step 4 of 5</div><h2 class="review-step-title">Check Next Actions</h2>';
      html += '<p class="review-step-prompt">Are these still your real next actions? Remove or update anything stale.</p>';
      html += '<div class="review-na-list">';
      function collectNextActions(cards, path) {
        const items = [];
        for (const card of cards) {
          if (card.archived || (ov.archivedCards && ov.archivedCards[card.id])) continue;
          const undone = (card.tasks || []).filter(t => !t.done);
          if (undone.length > 0) {
            const first = undone[0];
            const { displayText } = parseContext(first.text);
            items.push({ card, task: first, displayText });
          }
          if (card.children) items.push(...collectNextActions(card.children, [...path, card.id]));
        }
        return items;
      }
      const naItems = [];
      for (const col of data.columns) naItems.push(...collectNextActions(col.projects, []));
      if (naItems.length === 0) {
        html += '<div class="review-empty">No next actions found.</div>';
      } else {
        for (const na of naItems.slice(0, 15)) {
          html += `<div class="review-na-row"><span class="review-na-card">${e(na.card.name)}</span><span class="review-na-task">${e(na.displayText)}</span></div>`;
        }
      }
      html += '</div>';
      html += '<button class="review-next-btn" id="review-next">Next Actions confirmed \u2192</button>';
      content.innerHTML = html;
    } else if (_reviewStep === 4) {
      // Step 5 — Look ahead
      const now = new Date(); now.setHours(0,0,0,0);
      const week = new Date(now.getTime() + 7 * 86400000);
      let html = '<div class="review-step-label">Step 5 of 5</div><h2 class="review-step-title">Look ahead</h2>';
      html += '<p class="review-step-prompt">Anything coming up that needs a next action now?</p>';
      html += '<div class="review-upcoming-list">';
      const upcoming = [];
      function findUpcoming(cards) {
        for (const card of cards) {
          if (card.dueDate) {
            const due = new Date(card.dueDate + 'T00:00:00');
            if (due >= now && due <= week) upcoming.push({ name: card.name, dueDate: card.dueDate });
          }
          if (card.children) findUpcoming(card.children);
        }
      }
      for (const col of data.columns) findUpcoming(col.projects);
      upcoming.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
      if (upcoming.length === 0) {
        html += '<div class="review-empty">Nothing due in the next 7 days.</div>';
      } else {
        for (const u of upcoming) {
          const d = new Date(u.dueDate + 'T00:00:00');
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          html += `<div class="review-upcoming-row"><span class="review-upcoming-name">${e(u.name)}</span><span class="review-upcoming-date">${months[d.getMonth()]} ${d.getDate()}</span></div>`;
        }
      }
      html += '</div>';
      html += '<button class="review-next-btn" id="review-next">Review complete \u2192</button>';
      content.innerHTML = html;
    } else {
      // Done!
      const today = new Date().toISOString().split('T')[0];
      const ov2 = loadOverrides();
      ov2.lastWeeklyReview = today;
      saveOverrides(ov2);
      content.innerHTML = `
        <div class="review-done">
          <h2>Weekly Review done.</h2>
          <p class="review-done-date">${today}</p>
          <button class="review-back-btn" id="review-back">Back to board</button>
        </div>
      `;
      fill.style.width = '100%';
      document.getElementById('review-back').addEventListener('click', () => setReviewMode(false));
      return;
    }
    document.getElementById('review-next').addEventListener('click', () => {
      _reviewStep++;
      renderReviewStep();
    });
  }

// Update review button tooltip with last review date
export function updateReviewTooltip() {
    const ov = loadOverrides();
    const btn = document.getElementById('review-btn');
    if (!btn) return;
    if (ov.lastWeeklyReview) {
      const diff = Math.floor((Date.now() - new Date(ov.lastWeeklyReview + 'T00:00:00').getTime()) / 86400000);
      btn.title = `Weekly Review — last reviewed ${diff === 0 ? 'today' : diff === 1 ? 'yesterday' : diff + ' days ago'}`;
      btn.classList.toggle('review-due', diff > 7);
    } else {
      // Never reviewed — always show as due
      btn.title = 'Weekly Review — not yet done';
      btn.classList.add('review-due');
    }
  }

// ── Day 14: Focus / Work Mode ──────────────────────────────────────────────

export function enterWorkMode(cardId) {
    const data = mergeOverrides(boardData);
    const card = findCardById(cardId, data);
    if (!card) return;
    _workModeCardId = cardId;
    _workModeSeconds = 0;
    // Find the first uncompleted task (prefer next-action flagged)
    const tasks = (card.tasks || []).filter(t => !t.done);
    if (tasks.length === 0) return; // nothing to focus on
    _workModeTaskIdx = 0;

    // Show work mode
    document.getElementById('work-mode').classList.remove('hidden');
    document.getElementById('board').classList.add('hidden');
    document.getElementById('focus-view').classList.add('hidden');
    document.getElementById('wins-view').classList.add('hidden');
    document.getElementById('review-view').classList.add('hidden');
    document.getElementById('breadcrumb').classList.add('hidden');
    document.querySelector('header').classList.add('hidden');

    renderWorkMode(card, tasks);
    startWorkTimer();
  }

export function renderWorkMode(card, tasks) {
    const ov = loadOverrides();
    document.getElementById('work-card-name').textContent = card.name;
    const outcome = card.outcome || '';
    const outcomeEl = document.getElementById('work-outcome');
    outcomeEl.textContent = outcome;
    outcomeEl.classList.toggle('hidden', !outcome);

    if (tasks.length === 0) {
      document.getElementById('work-task').textContent = 'All tasks done \u2014 great work!';
      document.getElementById('work-done-btn').textContent = 'Exit';
      document.getElementById('work-done-btn').onclick = () => exitWorkMode();
      return;
    }

    const { displayText } = parseContext(tasks[0].text);
    document.getElementById('work-task').textContent = displayText;
    document.getElementById('work-done-btn').textContent = 'Done';
    document.getElementById('work-done-btn').onclick = () => {
      // Mark task complete
      const taskId = tasks[0].id;
      const ov2 = loadOverrides();
      if (!ov2.taskStates) ov2.taskStates = {};
      ov2.taskStates[taskId] = true;
      if (!ov2.taskCompletedAt) ov2.taskCompletedAt = {};
      ov2.taskCompletedAt[taskId] = new Date().toISOString();
      // Log win
      if (!ov2.completedTasks) ov2.completedTasks = [];
      ov2.completedTasks.push({ taskId, cardId: card.id, cardName: card.name, text: tasks[0].text, completedAt: new Date().toISOString() });
      if (ov2.completedTasks.length > 50) ov2.completedTasks = ov2.completedTasks.slice(-50);
      // Log work mode session
      if (!ov2.workModeSessions) ov2.workModeSessions = [];
      ov2.workModeSessions.push({ cardId: card.id, taskId, durationSeconds: _workModeSeconds, completedAt: new Date().toISOString() });
      if (ov2.workModeSessions.length > 100) ov2.workModeSessions = ov2.workModeSessions.slice(-100);
      saveOverrides(ov2);

      // Reset timer for next task
      _workModeSeconds = 0;
      updateWorkTimer();

      // Get fresh data and find remaining tasks
      const freshData = mergeOverrides(boardData);
      const freshCard = findCardById(_workModeCardId, freshData);
      if (!freshCard) { exitWorkMode(); return; }
      const remaining = (freshCard.tasks || []).filter(t => !t.done);
      renderWorkMode(freshCard, remaining);
    };
  }

export function startWorkTimer() {
    clearInterval(_workModeTimer);
    _workModeTimer = setInterval(() => {
      _workModeSeconds++;
      updateWorkTimer();
    }, 1000);
  }

export function updateWorkTimer() {
    const mins = Math.floor(_workModeSeconds / 60);
    const secs = _workModeSeconds % 60;
    document.getElementById('work-timer').textContent =
      String(mins).padStart(2, '0') + ':' + String(secs).padStart(2, '0');
  }

export function exitWorkMode() {
    clearInterval(_workModeTimer);
    _workModeCardId = null;
    document.getElementById('work-mode').classList.add('hidden');
    document.querySelector('header').classList.remove('hidden');
    renderFromState();
  }

// ── Focus Flow (§9b) ─────────────────────────────────────────────────────

export function buildQuickWinQueue() {
    const data = mergeOverrides(boardData);
    const ov = loadOverrides();
    const items = [];

    function collectQuickWins(cards, colId, colName, navPath) {
      for (const card of cards) {
        if (card.archived) continue;
        const status = normalizeStatus(card.status || '');
        if (status === 'Done' || status === 'On Hold' || status === 'Waiting') continue;
        const undone = (card.tasks || []).filter(t => !t.done);
        for (const task of undone) {
          if (task.ripe) continue; // diamonds excluded from quick wins
          const { displayText } = parseContext(task.text);
          // Full context path: Column › [parents ›] Card
          const fullPath = [colName, ...navPath.map(p => p.name), card.name];
          const breadcrumb = fullPath.join(' › ');
          items.push({ taskId: task.id, cardId: card.id, task, card, colId, colName, breadcrumb, displayText });
          break; // first undone non-diamond task only
        }
        if (card.children?.length > 0) {
          collectQuickWins(card.children, colId, colName, [...navPath, { id: card.id, name: card.name }]);
        }
      }
    }

    for (const col of data.columns) {
      if (ov.archivedColumns?.includes(col.id)) continue;
      collectQuickWins(col.projects || [], col.id, col.name, []);
    }

    // Sort: overdue first → due soonest → walk order (already in walk order)
    items.sort((a, b) => {
      const urgA = getDueDateUrgency(a.card.dueDate);
      const urgB = getDueDateUrgency(b.card.dueDate);
      const overA = urgA === 'overdue' ? 0 : 1;
      const overB = urgB === 'overdue' ? 0 : 1;
      if (overA !== overB) return overA - overB;
      // Both have dates — sort by date ascending
      if (a.card.dueDate && b.card.dueDate) return new Date(a.card.dueDate) - new Date(b.card.dueDate);
      if (a.card.dueDate && !b.card.dueDate) return -1;
      if (!a.card.dueDate && b.card.dueDate) return 1;
      return 0; // preserve walk order
    });

    return items.slice(0, 6);
  }

export function buildDeepWorkQueue(colId) {
    const data = mergeOverrides(boardData);
    const ov = loadOverrides();
    const items = [];
    const col = data.columns.find(c => c.id === colId);
    if (!col) return items;

    function collectDiamonds(cards, navPath) {
      for (const card of cards) {
        if (card.archived) continue;
        const status = normalizeStatus(card.status || '');
        if (status === 'Done' || status === 'On Hold' || status === 'Waiting') continue;
        const undone = (card.tasks || []).filter(t => !t.done);
        for (const task of undone) {
          if (!task.ripe) continue; // only diamonds
          const { displayText } = parseContext(task.text);
          // Full context path: Column › [parents ›] Card
          const fullPath = [col.name, ...navPath.map(p => p.name), card.name];
          const breadcrumb = fullPath.join(' › ');
          items.push({ taskId: task.id, cardId: card.id, task, card, colId, colName: col.name, breadcrumb, displayText });
          break; // first undone diamond task only
        }
        if (card.children?.length > 0) {
          collectDiamonds(card.children, [...navPath, { id: card.id, name: card.name }]);
        }
      }
    }

    collectDiamonds(col.projects || [], []);
    return items; // vertical order preserved
  }

export function enterFocusFlow() {
    if (_focusFlowActive) return;
    // Mobile guard
    if (typeof window !== 'undefined' && window.innerWidth <= 520) return;

    _focusFlowActive = true;
    _focusPhase = 1;
    _focusCompletedCount = 0;
    _focusStartedAt = Date.now();
    _focusSkippedIds = new Set();
    _focusTimeCapReached = false;
    _focusIntentionCols = [];
    _focusIntentionColIdx = 0;

    // Build Phase 1 queue
    _focusQueue = buildQuickWinQueue();
    _focusQueueIdx = 0;

    // Hide board, keep header visible, show focus flow
    document.getElementById('board').classList.add('hidden');
    document.getElementById('focus-view').classList.add('hidden');
    document.getElementById('wins-view').classList.add('hidden');
    document.getElementById('review-view').classList.add('hidden');
    document.getElementById('breadcrumb').classList.add('hidden');
    document.getElementById('focus-flow').classList.remove('hidden');

    // Silent 30-min timer — check every 10s
    clearInterval(_focusTimerCheck);
    _focusTimerCheck = setInterval(() => {
      if (_focusPhase === 1 && !_focusTimeCapReached) {
        if (Date.now() - _focusStartedAt >= 30 * 60 * 1000) {
          _focusTimeCapReached = true;
        }
      }
    }, 10000);

    // Wire button handlers
    document.getElementById('focus-complete-btn').onclick = completeFocusTask;
    document.getElementById('focus-skip-btn').onclick = skipFocusTask;
    document.getElementById('focus-diamond-btn').onclick = reclassifyToDiamond;
    document.getElementById('focus-exit-btn').onclick = exitFocusFlow;

    if (_focusQueue.length === 0) {
      // No quick wins — skip to Phase 2 transition
      focusTransition('Quick wins cleared. Time to set your intention.');
    } else {
      renderFocusCard();
    }
  }

export function exitFocusFlow() {
    _focusFlowActive = false;
    _focusPhase = 0;
    _focusQueue = [];
    _focusQueueIdx = 0;
    _focusSkippedIds = new Set();
    clearInterval(_focusTimerCheck);
    _focusTimerCheck = null;

    // Stop intentions if they were started during Focus Flow
    if (_intentionsActive) stopIntentions();

    document.getElementById('focus-flow').classList.add('hidden');
    document.getElementById('board').classList.remove('hidden');
    renderFromState();
  }

export function renderFocusCard() {
    const inner = document.querySelector('.focus-flow-inner');
    const phaseLabel = document.getElementById('focus-phase-label');
    const progress = document.getElementById('focus-progress');
    const card = document.getElementById('focus-card');
    const breadcrumb = document.getElementById('focus-card-breadcrumb');
    const taskEl = document.getElementById('focus-card-task');
    const actions = document.getElementById('focus-actions');
    const diamondBtn = document.getElementById('focus-diamond-btn');

    card.classList.remove('transition', 'completing');
    actions.classList.remove('hidden');

    // Phase class for Tim Brown's differentiated sizing
    inner.classList.toggle('phase-deep', _focusPhase === 3);

    if (_focusPhase === 1) {
      phaseLabel.textContent = 'Quick Wins';
      diamondBtn.classList.remove('hidden');

      // Progress dots — no text label (Sullivan: dots alone, no Gap signal)
      progress.classList.remove('hidden');
      let dotsHtml = '';
      const total = Math.min(_focusQueue.length, 6);
      for (let i = 0; i < total; i++) {
        const cls = i < _focusCompletedCount ? 'done' : (i === _focusCompletedCount ? 'current' : '');
        dotsHtml += `<span class="focus-dot ${cls}"></span>`;
      }
      progress.innerHTML = dotsHtml;

      // Current task
      const item = _focusQueue[_focusQueueIdx];
      if (!item) { checkPhase1End(); return; }
      breadcrumb.textContent = item.breadcrumb;
      taskEl.textContent = item.displayText;

    } else if (_focusPhase === 3) {
      phaseLabel.textContent = 'Deep Work';
      diamondBtn.classList.add('hidden');
      progress.classList.add('hidden');

      const item = _focusQueue[_focusQueueIdx];
      if (!item) { advanceDeepWorkColumn(); return; }
      breadcrumb.textContent = item.breadcrumb;
      taskEl.textContent = item.displayText;
    }
  }

// Completion animation — fade out, swap content, fade in (Norman: 250ms feedback moment)
function animateCardTransition(callback) {
    const card = document.getElementById('focus-card');
    card.classList.add('completing');
    setTimeout(() => {
      callback();
      card.classList.remove('completing');
    }, 250);
  }

function checkPhase1End() {
    // Dawson metacognition: show what was completed + pattern data
    const skippedCount = _focusSkippedIds.size;
    let message = `You cleared ${_focusCompletedCount} quick win${_focusCompletedCount !== 1 ? 's' : ''}.`;
    if (skippedCount > 0) {
      // Find which columns were skipped most
      const skipCols = {};
      for (const item of _focusQueue) {
        if (_focusSkippedIds.has(item.taskId)) {
          skipCols[item.colName] = (skipCols[item.colName] || 0) + 1;
        }
      }
      const topSkipCol = Object.entries(skipCols).sort((a, b) => b[1] - a[1])[0];
      if (topSkipCol) {
        message += ` You skipped ${skippedCount} in ${topSkipCol[0]}.`;
      }
    }
    message += ' Time to set your intention.';
    focusTransition(message);
  }

function advanceDeepWorkColumn() {
    _focusIntentionColIdx++;
    if (_focusIntentionColIdx < _focusIntentionCols.length) {
      const nextCol = _focusIntentionCols[_focusIntentionColIdx];
      _focusQueue = buildDeepWorkQueue(nextCol.colId);
      _focusQueueIdx = 0;
      if (_focusQueue.length > 0) {
        renderFocusCard();
        return;
      }
      // This column also empty — try next
      advanceDeepWorkColumn();
      return;
    }
    // All columns exhausted
    focusTransition('All done. Nice work.');
  }

export function completeFocusTask() {
    const item = _focusQueue[_focusQueueIdx];
    if (!item) return;

    // Mark task done (same pattern as Work Mode line 1147-1163)
    const ov = loadOverrides();
    if (!ov.taskStates) ov.taskStates = {};
    ov.taskStates[item.taskId] = true;
    if (!ov.taskCompletedAt) ov.taskCompletedAt = {};
    ov.taskCompletedAt[item.taskId] = new Date().toISOString();
    if (!ov.completedTasks) ov.completedTasks = [];
    ov.completedTasks.push({
      taskId: item.taskId, cardId: item.cardId, cardName: item.card.name,
      text: item.task.text, completedAt: new Date().toISOString()
    });
    if (ov.completedTasks.length > 50) ov.completedTasks = ov.completedTasks.slice(-50);
    saveOverrides(ov);

    _focusCompletedCount++;
    _focusQueueIdx++;

    // Animate card transition (Norman: 250ms feedback moment)
    animateCardTransition(() => {
      if (_focusPhase === 1) {
        if (_focusCompletedCount >= 6 || _focusQueueIdx >= _focusQueue.length || _focusTimeCapReached) {
          checkPhase1End();
        } else {
          renderFocusCard();
        }
      } else if (_focusPhase === 3) {
        if (_focusQueueIdx >= _focusQueue.length) {
          advanceDeepWorkColumn();
        } else {
          renderFocusCard();
        }
      }
    });
  }

export function skipFocusTask() {
    const item = _focusQueue[_focusQueueIdx];
    if (!item) return;
    _focusSkippedIds.add(item.taskId);
    _focusQueueIdx++;

    animateCardTransition(() => {
      if (_focusPhase === 1) {
        if (_focusQueueIdx >= _focusQueue.length || _focusTimeCapReached) {
          checkPhase1End();
        } else {
          renderFocusCard();
        }
      } else if (_focusPhase === 3) {
        if (_focusQueueIdx >= _focusQueue.length) {
          advanceDeepWorkColumn();
        } else {
          renderFocusCard();
        }
      }
    });
  }

export function reclassifyToDiamond() {
    const item = _focusQueue[_focusQueueIdx];
    if (!item) return;
    // Set diamond flag
    const ov = loadOverrides();
    if (!ov.taskRipe) ov.taskRipe = {};
    ov.taskRipe[item.taskId] = true;
    saveOverrides(ov);

    // Advance (don't count as completed)
    _focusQueueIdx++;
    animateCardTransition(() => {
      if (_focusPhase === 1) {
        if (_focusQueueIdx >= _focusQueue.length || _focusTimeCapReached) {
          checkPhase1End();
        } else {
          renderFocusCard();
        }
      }
    });
  }

function focusTransition(message) {
    const phaseLabel = document.getElementById('focus-phase-label');
    const progress = document.getElementById('focus-progress');
    const card = document.getElementById('focus-card');
    const breadcrumb = document.getElementById('focus-card-breadcrumb');
    const taskEl = document.getElementById('focus-card-task');
    const actions = document.getElementById('focus-actions');

    phaseLabel.textContent = '';
    progress.classList.add('hidden');
    card.classList.add('transition');
    breadcrumb.textContent = '';
    actions.classList.add('hidden');

    // "All done" has exit instead of continue
    if (message === 'All done. Nice work.') {
      taskEl.innerHTML = `<div>${e(message)}</div>
        <button class="focus-continue-btn" id="focus-finish-btn">Back to Overview</button>`;
      document.getElementById('focus-finish-btn').onclick = exitFocusFlow;
      return;
    }

    taskEl.innerHTML = `<div>${e(message)}</div>
      <button class="focus-continue-btn" id="focus-continue-btn">Continue</button>`;

    document.getElementById('focus-continue-btn').onclick = () => {
      if (_focusPhase === 1 || _focusPhase === 0) {
        // Transition to Phase 2: open intentions
        _focusPhase = 2;
        document.getElementById('focus-flow').classList.add('hidden');
        openIntentionsModal();
      } else if (_focusPhase === 2) {
        // Transition to Phase 3: deep work
        _focusPhase = 3;
        _focusQueue = buildDeepWorkQueue(_focusIntentionCols[0].colId);
        _focusQueueIdx = 0;
        _focusIntentionColIdx = 0;

        document.getElementById('focus-flow').classList.remove('hidden');

        if (_focusQueue.length === 0) {
          focusTransition(`No diamond tasks in ${_focusIntentionCols[0].colName}. Tag tasks with 🔷 to queue them.`);
        } else {
          renderFocusCard();
        }
      }
    };
  }

// Called from startIntentions() when Focus Flow is active
export function onIntentionsStartedDuringFocusFlow(intentionsList) {
    _focusIntentionCols = intentionsList;
    _focusIntentionColIdx = 0;
    _focusPhase = 2;

    // Stop the normal intentions timer/spotlight — Focus Flow handles its own rendering
    stopIntentions();

    // Show focus flow, transition to Phase 3
    document.getElementById('focus-flow').classList.remove('hidden');
    focusTransition(`Intention set: ${intentionsList.map(i => i.colName).join(', ')}. Let\u2019s go deep.`);
  }

// ── Day 16: Daily Intentions ──

export function openIntentionsModal() {
    const data = mergeOverrides(boardData);
    const ov = loadOverrides();
    const modal = document.getElementById('intentions-modal');
    const list = document.getElementById('intentions-col-list');
    const cols = data.columns.filter(c => !ov.archivedColumns?.includes(c.id));
    let selected = [];

    function renderModal() {
      list.innerHTML = cols.map(col => {
        const checked = selected.includes(col.id);
        const budgetId = `intent-budget-${col.id}`;
        return `<div class="intentions-col-row${checked ? ' selected' : ''}">
          <label class="intentions-col-check">
            <input type="checkbox" class="intent-col-cb" data-col-id="${col.id}" ${checked ? 'checked' : ''}>
            <span class="intentions-col-name">${e(col.name)}</span>
          </label>
          ${checked ? `<select class="intent-budget-sel" id="${budgetId}" data-col-id="${col.id}">

            <option value="30">30 min</option>
            <option value="60" selected>1 hour</option>
            <option value="90">1.5 hours</option>
            <option value="120">2 hours</option>
            <option value="180">3 hours</option>
            <option value="240">4 hours</option>
          </select>` : ''}
        </div>`;
      }).join('');

      list.querySelectorAll('.intent-col-cb').forEach(cb => {
        cb.addEventListener('change', () => {
          const colId = cb.dataset.colId;
          if (cb.checked) {
            if (selected.length >= 3) { cb.checked = false; return; }
            selected.push(colId);
          } else {
            selected = selected.filter(id => id !== colId);
          }
          renderModal();
        });
      });

      document.getElementById('intentions-start-btn').disabled = selected.length === 0;
    }

    renderModal();
    modal.classList.remove('hidden');

    document.getElementById('intentions-start-btn').onclick = () => {
      const intentions = selected.map(colId => {
        const col = cols.find(c => c.id === colId);
        const budgetSel = document.getElementById(`intent-budget-${colId}`);
        return { colId, colName: col.name, budgetMinutes: parseInt(budgetSel?.value || 60), elapsedSeconds: 0 };
      });
      modal.classList.add('hidden');
      startIntentions(intentions);
    };

    document.getElementById('intentions-cancel-btn').onclick = () => {
      modal.classList.add('hidden');
      if (_focusFlowActive) {
        document.getElementById('focus-flow').classList.remove('hidden');
        focusTransition('Quick wins cleared. Time to set your intention.');
      }
    };
    modal.addEventListener('click', ev => {
      if (ev.target === modal) {
        modal.classList.add('hidden');
        if (_focusFlowActive) {
          document.getElementById('focus-flow').classList.remove('hidden');
          focusTransition('Quick wins cleared. Time to set your intention.');
        }
      }
    });
  }

export function applyIntentionSpotlight() {
    const focusColId = (_intentionsActive && _intentionIdx < _intentions.length)
      ? _intentions[_intentionIdx].colId : null;

    // Dim non-focus columns
    document.querySelectorAll('.column').forEach(col => {
      const isActive = !focusColId || col.dataset.columnId === focusColId;
      col.classList.toggle('intention-dim', !isActive);
    });

    // Within the focus column, spotlight top incomplete card, dim the rest
    document.querySelectorAll('.card').forEach(c => c.classList.remove('card-intention-dim'));
    if (focusColId) {
      const activeCol = document.querySelector(`.column[data-column-id="${focusColId}"]`);
      if (activeCol) {
        const cards = Array.from(activeCol.querySelectorAll('.card'));
        let spotlitIdx = cards.findIndex(c =>
          c.querySelector('input[type="checkbox"]:not(:checked)')
        );
        if (spotlitIdx === -1) spotlitIdx = 0; // fallback to first card
        cards.forEach((c, i) => {
          if (i !== spotlitIdx) c.classList.add('card-intention-dim');
        });
      }
    }
  }

export function startIntentions(intentionsList) {
    // Focus Flow intercept — bridge to Phase 3 instead of normal spotlight
    if (_focusFlowActive) {
      onIntentionsStartedDuringFocusFlow(intentionsList);
      return;
    }

    // Always return to board view when starting a focus session
    if (_focusMode) setFocusMode(false);
    if (_reviewMode) setReviewMode(false);
    if (_winsMode) setWinsMode(false);

    _intentions = intentionsList;
    _intentionIdx = 0;
    _intentionsActive = true;
    document.getElementById('intentions-btn').innerHTML = '■ End Focus';
    _intentionTransitionPending = false;
    document.getElementById('intentions-bar').classList.remove('hidden');
    document.getElementById('intentions-transition').classList.add('hidden');
    updateIntentionsBar();
    clearInterval(_intentionTimerId);
    _intentionTimerId = setInterval(() => {
      if (!_intentionsActive || _intentionIdx >= _intentions.length) return;
      _intentions[_intentionIdx].elapsedSeconds++;
      updateIntentionsBar();
      checkIntentionTransition();
    }, 1000);
    setTimeout(applyIntentionSpotlight, 50);
    syncToolbarActive();
  }

export function stopIntentions() {
    _intentionsActive = false;
    clearInterval(_intentionTimerId);
    _intentionTimerId = null;
    document.getElementById('intentions-bar').classList.add('hidden');
    document.getElementById('intentions-transition').classList.add('hidden');
    document.getElementById('intentions-btn').innerHTML = '🎯 Intentions';
    document.querySelectorAll('.column').forEach(col => col.classList.remove('intention-dim'));
    document.querySelectorAll('.card').forEach(c => c.classList.remove('card-intention-dim'));
    if (_focusMode) renderNextActions();
    syncToolbarActive();
  }

export function updateIntentionsBar() {
    const areasEl = document.getElementById('intentions-bar-areas');
    const timerEl = document.getElementById('intentions-bar-timer');
    if (!areasEl || !timerEl) return;

    areasEl.innerHTML = _intentions.map((int, i) => {
      const cls = i < _intentionIdx ? 'intent-dot-done' : i === _intentionIdx ? 'intent-dot-active' : 'intent-dot-pending';
      return `<span class="intent-dot ${cls}">${e(int.colName)}</span>`;
    }).join('');

    if (_intentionIdx < _intentions.length) {
      const cur = _intentions[_intentionIdx];
      const budget = cur.budgetMinutes * 60;
      const elapsed = cur.elapsedSeconds || 0;
      const remaining = budget - elapsed;
      const over = remaining < 0;
      const abs = Math.abs(remaining);
      const mm = Math.floor(abs / 60);
      const ss = String(abs % 60).padStart(2, '0');
      timerEl.textContent = over ? `+${mm}:${ss} over` : `${mm}:${ss} left`;
      timerEl.className = 'intentions-bar-timer' + (over ? ' intent-over' : '');
    }
  }

export function checkIntentionTransition() {
    if (!_intentionsActive || _intentionTransitionPending) return;
    if (_intentionIdx >= _intentions.length) return;
    const cur = _intentions[_intentionIdx];
    const budget = cur.budgetMinutes * 60;
    const elapsed = cur.elapsedSeconds || 0;
    const timeUp = elapsed >= budget;

    // Check if current area has any more next actions
    const data = mergeOverrides(boardData);
    const col = data.columns.find(c => c.id === cur.colId);
    let hasNAs = false;
    if (col) {
      function checkNAs(cards) {
        for (const card of cards) {
          if (hasNAs) return;
          if (card.archived || card.status === 'Done' || card.status === 'On Hold') continue;
          if ((card.tasks || []).some(t => !t.done)) { hasNAs = true; return; }
          if (card.children?.length > 0) checkNAs(card.children);
        }
      }
      checkNAs(col.projects || []);
    }

    const allClear = !hasNAs;
    const nextIdx = _intentionIdx + 1;
    const hasNext = nextIdx < _intentions.length;

    if (timeUp || allClear) {
      if (hasNext) {
        _intentionTransitionPending = true;
        const transEl = document.getElementById('intentions-transition');
        const msg = allClear
          ? `All done with ${cur.colName} — move on to ${_intentions[nextIdx].colName}?`
          : `Time's up on ${cur.colName} — move on to ${_intentions[nextIdx].colName}?`;
        document.getElementById('intentions-transition-msg').textContent = msg;
        transEl.classList.remove('hidden');
        // Auto-advance silently after 30s if ignored
        setTimeout(() => {
          if (_intentionTransitionPending) advanceIntention(true);
        }, 30000);
      } else if (allClear) {
        // All intentions complete
        _intentionTransitionPending = true;
        document.getElementById('intentions-transition-msg').textContent = 'All done for today — great work! 🎉';
        document.getElementById('intentions-transition').classList.remove('hidden');
        document.getElementById('intentions-advance-btn').textContent = 'Finish';
        setTimeout(() => stopIntentions(), 5000);
      }
    }
  }

export function advanceIntention(silent = false) {
    _intentionTransitionPending = false;
    document.getElementById('intentions-transition').classList.add('hidden');
    _intentionIdx++;
    if (_intentionIdx >= _intentions.length) { stopIntentions(); return; }
    updateIntentionsBar();
    setTimeout(applyIntentionSpotlight, 50);
    if (!silent && _focusMode) renderNextActions();
  }

// ── Focus view (full-board Next Actions) ──────────────────────────────────

export function setFocusMode(on) {
    _focusMode = on;
    document.getElementById('board').classList.toggle('hidden', on);
    document.getElementById('review-view').classList.add('hidden');
    if (on) { _reviewMode = false; }
    document.getElementById('focus-view').classList.toggle('hidden', !on);
    document.getElementById('wins-view').classList.add('hidden');
    if (on) _winsMode = false;
    document.getElementById('breadcrumb').classList.toggle('hidden', on || navStack.length === 0);
    document.getElementById('next-actions-btn').innerHTML = '⚡ Next Actions';
    document.getElementById('overview-back-btn').classList.toggle('hidden', !on);
    document.querySelector('header').classList.toggle('focus-mode-active', on);
    if (on) {
      populateNaFilters();
      renderNextActions();
    }
    syncToolbarActive();
  }

export function populateNaFilters() {
    const ov = loadOverrides();
    const data = mergeOverrides(boardData);

    // Column filter
    const colSel = document.getElementById('na-column-filter');
    const prevCol = colSel.value;
    colSel.innerHTML = '<option value="all">All areas</option>';
    for (const col of data.columns) {
      if (ov.archivedColumns?.includes(col.id)) continue;
      const displayName = (ov.columnRenames?.[col.id]) || col.name;
      const opt = document.createElement('option');
      opt.value = col.id; opt.textContent = displayName;
      colSel.appendChild(opt);
    }
    if (prevCol) colSel.value = prevCol;
  }

// Context grouping order (same as CTX_TAGS module constant)
const NA_CTX_ORDER = CTX_TAGS;

export function renderNextActions() {
    const list = document.getElementById('next-actions-list');
    const filterColId = document.getElementById('na-column-filter').value;
    const maxCount = Math.max(1, parseInt(document.getElementById('na-count').value) || 5);
    const ov = loadOverrides();
    const data = mergeOverrides(boardData);

    function collectCards(cards, navPath) {
      const items = [];
      for (const card of cards) {
        if (card.archived) continue;
        const _cardStatus = normalizeStatus(card.status || '');
        if (_cardStatus === 'Done' || _cardStatus === 'On Hold') continue; // not actionable
        const undone = (card.tasks || []).filter(t => !t.done);
        for (const task of undone) {
          const { context, displayText } = parseContext(task.text);
          items.push({ card, task, context, displayText, navPath: [...navPath] });
          break; // first undone task only
        }
        if (card.children?.length > 0) {
          items.push(...collectCards(card.children, [...navPath, { id: card.id, name: card.name }]));
        }
      }
      return items;
    }

    // Day 16: when intentions active, restrict to current focus column
    const intentionColId = (_intentionsActive && _intentionIdx < _intentions.length)
      ? _intentions[_intentionIdx].colId : null;

    let allItems = [];
    for (const col of data.columns) {
      if (ov.archivedColumns?.includes(col.id)) continue;
      if (filterColId !== 'all' && col.id !== filterColId) continue;
      if (intentionColId && col.id !== intentionColId) continue;
      const colItems = collectCards(col.projects || [], []);
      colItems.forEach(i => { i.colId = col.id; i.colName = col.name; });
      allItems.push(...colItems);
    }

    const shown = allItems.slice(0, maxCount);

    if (shown.length === 0) {
      list.innerHTML = '<div class="next-actions-empty">Nothing here — try a different filter.</div>';
      return;
    }

    // Group by context
    const groups = {};
    for (const ctx of NA_CTX_ORDER) groups[ctx] = [];
    groups['waiting'] = [];
    groups['other'] = [];

    for (const item of shown) {
      const ctx = (item.context || '').toLowerCase();
      if (ctx === 'waiting') {
        groups['waiting'].push(item);
      } else if (NA_CTX_ORDER.includes(ctx)) {
        groups[ctx].push(item);
      } else {
        groups['other'].push(item);
      }
    }

    const sectionOrder = [...NA_CTX_ORDER, 'waiting', 'other'];
    const nonEmptySections = sectionOrder.filter(ctx => groups[ctx].length > 0);
    const onlyGeneral = nonEmptySections.length === 1 && nonEmptySections[0] === 'other';
    let globalIdx = 1;
    let html = '';

    for (const ctx of sectionOrder) {
      const items = groups[ctx];
      if (items.length === 0) continue;

      const isWaiting = ctx === 'waiting';
      const isOther = ctx === 'other';
      const label = isOther ? 'General' : isWaiting ? 'Waiting' : `@${ctx}`;
      const showHeader = !onlyGeneral; // hide header when everything is untagged

      html += `<div class="na-group${isWaiting ? ' na-group-waiting' : ''}">`;
      if (showHeader) {
        html += `<div class="na-group-header">
          <span class="na-group-label">${label}</span>
          <span class="na-group-count">${items.length}</span>
        </div>`;
      }

      for (const item of items) {
        const due = item.card.dueDate;
        const urgency = getDueDateUrgency(due);
        const dueLabel = due ? `<span class="na-due-pill urgency-${urgency}">${urgency === 'overdue' ? 'Overdue' : formatShortDate(due)}</span>` : '';
        const outcome = item.card.outcome;
        html += `
        <button class="na-row na-row-simple${isWaiting ? ' na-row-waiting' : ''}" data-card-id="${item.card.id}" data-nav-path='${JSON.stringify(item.navPath)}'>
          <span class="na-row-num">${globalIdx++}</span>
          <span class="na-row-body">
            <span class="na-row-card">${e(item.card.name)}${filterColId === 'all' ? ` <span class="na-row-col-inline">${e(item.colName)}</span>` : ''}</span>
            ${outcome ? `<span class="na-row-scope">${e(outcome)}</span>` : ''}
            <span class="na-row-task">${e(item.displayText)}</span>
          </span>
          ${dueLabel}
          <span class="na-row-arrow">→</span>
        </button>`;
      }

      html += `</div>`;
    }

    list.innerHTML = html;

    list.querySelectorAll('[data-card-id]').forEach(btn => {
      btn.addEventListener('click', () => {
        const cardId = btn.dataset.cardId;
        const navPath = JSON.parse(btn.dataset.navPath || '[]');
        setFocusMode(false);
        setNavStack(navPath);
        renderFromState();
        setTimeout(() => {
          const el = document.querySelector(`.card[data-project-id="${cardId}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('na-highlight');
            setTimeout(() => el.classList.remove('na-highlight'), 1800);
          }
        }, 150);
      });
    });
  }

// ── Keyboard navigation ──

export function getKbCards() {
    return Array.from(document.querySelectorAll('#board .card:not(.intention-dim)'));
  }

export function setKbFocus(idx) {
    const cards = getKbCards();
    if (!cards.length) return;
    _kbCardIdx = Math.max(0, Math.min(idx, cards.length - 1));
    cards.forEach((c, i) => c.classList.toggle('kb-focused', i === _kbCardIdx));
    cards[_kbCardIdx].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

export function clearKbFocus() {
    _kbCardIdx = -1;
    document.querySelectorAll('.kb-focused').forEach(c => c.classList.remove('kb-focused'));
  }
